import { useMemo, useState } from "react";
import { Search, Download, BookOpen } from "lucide-react";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";
import { RESOURCES, RESOURCE_CATEGORIES } from "../data/content";

export default function Resources() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    return RESOURCES.filter((r) => {
      const matchesCategory = category === "All" || r.category === category;
      const q = query.toLowerCase();
      const matchesQuery = !q || r.title.toLowerCase().includes(q) || r.summary.toLowerCase().includes(q) || r.category.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  const downloadChecklist = (r) => {
    const content = `${r.title}\nCF Full Circle — Immigration & Integration Center\n\n${r.summary}\n\nChecklist:\n${r.checklist.map((c) => `[ ] ${c}`).join("\n")}\n\nThis resource is general information, not legal advice. For guidance on your situation, start your journey at CF Full Circle.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${r.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="resources-page">
      <PageHero
        eyebrow="Resource Center"
        title="Clear guides for every step"
        lead="Searchable, plain-English resources and downloadable checklists. These are general information — not personalized legal advice."
      />

      <section className="py-12 lg:py-16" data-testid="resources-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center mb-8">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-mutedink" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search resources..."
                aria-label="Search resources"
                data-testid="resource-search-input"
                className="w-full h-11 rounded-full border border-line bg-surface pl-10 pr-4 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-teal"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {["All", ...RESOURCE_CATEGORIES].map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`resource-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${category === c ? "bg-teal text-white" : "bg-surface border border-line text-mutedink hover:border-teal hover:text-teal"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-mutedink py-12 text-center" data-testid="resources-empty">No resources match your search. Try a different term.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((r) => (
                <article key={r.id} className="bg-surface border border-line rounded-xl p-6 shadow-sm hover:shadow-md hover:border-teal transition-all flex flex-col" data-testid={`resource-card-${r.id}`}>
                  <span className="bg-teal-light text-teal font-medium text-xs px-3 py-1 rounded-full w-fit mb-4">{r.category}</span>
                  <h3 className="text-base font-sans font-semibold text-navy flex items-start gap-2">
                    <BookOpen className="h-4 w-4 text-teal flex-shrink-0 mt-1" /> {r.title}
                  </h3>
                  <p className="mt-2 text-sm text-mutedink leading-relaxed flex-1">{r.summary}</p>
                  <button
                    onClick={() => downloadChecklist(r)}
                    data-testid={`resource-download-${r.id}`}
                    className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-teal hover:text-teal-dark transition-colors"
                  >
                    <Download className="h-4 w-4" /> Download checklist
                  </button>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTASection title="Can't find what you're looking for?" text="Every situation is different. Tell us yours and we will point you in the right direction." />
    </div>
  );
}
